import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ImgHTMLAttributes,
  type ReactNode,
} from "react";
import {
  Link,
  NavLink,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  AtSign,
  Baby,
  BadgeCheck,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleUserRound,
  Clock3,
  FileText,
  Grid2X2,
  Headphones,
  Heart,
  Languages,
  List,
  Mail,
  MapPin,
  Menu,
  MessageCircle,
  Minus,
  Moon,
  PackageCheck,
  Phone,
  Plus,
  Search,
  Send,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Sparkles,
  Stethoscope,
  Sun,
  Trash2,
  UploadCloud,
  X,
  Zap,
} from "lucide-react";
import { copy, useStore } from "./store";
import {
  business,
  businessLinks,
  cartInquiryMessage,
  mapEmbedUrl,
  prescriptionInquiryMessage,
  productInquiryMessage,
  whatsappUrl,
} from "./config/business";
import { categoryBySlug, categoryDefinitions } from "./catalog";
import {
  buildStructuredData,
  canonicalForPath,
  getSeoPage,
  pageText,
  robotsForPage,
  siteName,
  socialImageUrl,
} from "./seo/seo";
import type { Product } from "./types";

const categoryIcons = [Zap, Sparkles, Baby, Stethoscope];
const categoryTones = ["berry", "rose", "sand", "sage"];
const categories = categoryDefinitions.map((category, index) => ({
  ...category,
  Icon: categoryIcons[index],
  tone: categoryTones[index],
}));
const categoryTree = categoryDefinitions;
const primaryCategories = categories.filter((category) =>
  ["supplements", "beauty-care", "mother-baby"].includes(category.slug),
);
const normalizeSearchText = (value: string) =>
  value
    .normalize("NFKD")
    .replace(/[\u064B-\u065F\u0670]/g, "")
    .replace(/[أإآ]/g, "ا")
    .replace(/ى/g, "ي")
    .replace(/ة/g, "ه")
    .toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
const productSearchText = (product: Product) =>
  normalizeSearchText(
    [
      product.nameAr,
      product.nameEn,
      product.brand,
      product.categoryAr,
      product.category,
      product.subcategory,
      ...(product.searchableTerms || []),
      ...(product.tags || []),
    ]
      .filter(Boolean)
      .join(" "),
  );

function useText() {
  const { lang } = useStore();
  return {
    lang,
    t: copy[lang],
    isAr: lang === "ar",
    pick: (ar: string, en: string) => (lang === "ar" ? ar : en),
  };
}

function PageMeta({
  title,
  description,
}: {
  title?: string;
  description?: string;
}) {
  const location = useLocation();
  const { lang } = useStore();
  useEffect(() => {
    const page = getSeoPage(location.pathname);
    const configuredText = page ? pageText(page, lang) : undefined;
    const fallbackDescription =
      lang === "ar"
        ? `${business.businessNameAr} في عمّان، الأردن. استفسر عن المنتجات والأسعار عبر واتساب.`
        : `${business.businessNameEn} in Amman, Jordan. Ask about products and prices via WhatsApp.`;
    const resolvedTitle =
      configuredText?.title ||
      (title
        ? `${title} | ${business.businessNameEn}`
        : business.businessNameEn);
    const resolvedDescription =
      configuredText?.description || description || fallbackDescription;
    const canonicalUrl = canonicalForPath(location.pathname);
    const isInternalSearch =
      location.pathname === "/shop" && location.search.length > 0;
    const robots = isInternalSearch ? "noindex,follow" : robotsForPage(page);
    const locale =
      configuredText?.locale || (lang === "ar" ? "ar_JO" : "en_US");
    const alternateLocale =
      configuredText?.alternateLocale || (lang === "ar" ? "en_US" : "ar_JO");
    const imageAlt =
      configuredText?.imageAlt ||
      (lang === "ar"
        ? `واجهة ${business.businessNameAr} في عمّان`
        : `${business.businessNameEn} storefront in Amman`);

    const upsertMeta = (
      attribute: "name" | "property",
      key: string,
      content: string,
    ) => {
      let meta = document.head.querySelector<HTMLMetaElement>(
        `meta[${attribute}="${key}"]`,
      );
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute(attribute, key);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    document.title = resolvedTitle;
    upsertMeta("name", "description", resolvedDescription);
    upsertMeta("name", "robots", robots);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", siteName);
    upsertMeta("property", "og:title", resolvedTitle);
    upsertMeta("property", "og:description", resolvedDescription);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:image", socialImageUrl);
    upsertMeta("property", "og:image:alt", imageAlt);
    upsertMeta("property", "og:image:type", "image/jpeg");
    upsertMeta("property", "og:image:width", "2048");
    upsertMeta("property", "og:image:height", "1101");
    upsertMeta("property", "og:locale", locale);
    upsertMeta("property", "og:locale:alternate", alternateLocale);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", resolvedTitle);
    upsertMeta("name", "twitter:description", resolvedDescription);
    upsertMeta("name", "twitter:image", socialImageUrl);
    upsertMeta("name", "twitter:image:alt", imageAlt);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);

    const existingSchema = document.getElementById("seo-jsonld");
    if (page) {
      const schema =
        existingSchema ||
        document.head.appendChild(document.createElement("script"));
      schema.id = "seo-jsonld";
      schema.setAttribute("type", "application/ld+json");
      schema.textContent = JSON.stringify(buildStructuredData(page, lang));
    } else {
      existingSchema?.remove();
    }

    window.scrollTo({ top: 0, behavior: "instant" });
  }, [title, description, lang, location.pathname, location.search]);
  return null;
}

function Logo() {
  return (
    <Link to="/" className="logo" aria-label="Marka Pharmacy home">
      <span className="logo-mark">
        <span>+</span>
      </span>
      <span className="logo-type">
        <b>MARKA</b>
        <small>PHARMACY · صيدلية ماركا الجنوبية</small>
      </span>
    </Link>
  );
}

function OfficialLogo({ className = "" }: { className?: string }) {
  return (
    <img
      className={`official-logo ${className}`.trim()}
      src={business.assets.logo}
      alt={`${business.businessNameEn} - ${business.businessNameAr}`}
    />
  );
}

function ProductImage({
  src,
  alt,
  onError,
  ...props
}: ImgHTMLAttributes<HTMLImageElement>) {
  const placeholder = "/assets/product-placeholder.svg";
  const [resolvedSrc, setResolvedSrc] = useState(
    typeof src === "string" && src ? src : placeholder,
  );

  useEffect(() => {
    setResolvedSrc(typeof src === "string" && src ? src : placeholder);
  }, [src]);

  return (
    <img
      {...props}
      src={resolvedSrc}
      alt={alt}
      onError={(event) => {
        if (resolvedSrc !== placeholder) setResolvedSrc(placeholder);
        onError?.(event);
      }}
    />
  );
}

function SocialLinks({ className = "" }: { className?: string }) {
  return (
    <div className={`social-link-list ${className}`.trim()}>
      <a
        href={business.facebookUrl}
        target="_blank"
        rel="noreferrer noopener"
        aria-label="Marka Pharmacy on Facebook"
      >
        <span className="social-glyph" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M14.5 8.5V6.7c0-.9.6-1.2 1.5-1.2h1.8V2.2c-.8-.1-1.8-.2-2.9-.2-2.9 0-4.9 1.8-4.9 5v1.5H7v3.8h3V22h4.5v-9.7h3l.5-3.8h-3.5Z" />
          </svg>
        </span>
        Facebook
      </a>
      <a
        href={business.instagramUrl}
        target="_blank"
        rel="noreferrer noopener"
        aria-label="Marka Pharmacy on Instagram"
      >
        <span className="social-glyph instagram-glyph" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <rect x="3" y="3" width="18" height="18" rx="5" />
            <circle cx="12" cy="12" r="4.2" />
            <circle
              cx="17.5"
              cy="6.5"
              r="1"
              fill="currentColor"
              stroke="none"
            />
          </svg>
        </span>
        Instagram
      </a>
      <a
        href={business.threadsUrl}
        target="_blank"
        rel="noreferrer noopener"
        aria-label="Marka Pharmacy on Threads"
      >
        <span className="social-glyph" aria-hidden="true">
          <AtSign />
        </span>
        Threads
      </a>
    </div>
  );
}

function LocationMap({ className = "" }: { className?: string }) {
  const { pick } = useText();
  return (
    <div className={`location-map ${className}`.trim()}>
      <iframe
        src={mapEmbedUrl}
        title={pick(
          "خريطة موقع صيدلية ماركا الجنوبية في عمّان",
          "Map showing Marka Pharmacy in Amman",
        )}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        allowFullScreen
      />
      <a
        className="button primary map-directions"
        href={business.mapsUrl}
        target="_blank"
        rel="noreferrer noopener"
      >
        <MapPin /> {pick("الاتجاهات عبر Google Maps", "Get Directions")}
      </a>
    </div>
  );
}

function SearchPanel({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { products, lang } = useStore();
  const { t, pick } = useText();
  const [query, setQuery] = useState("");
  const results = useMemo(
    () =>
      query.trim().length < 2
        ? products.slice(0, 5)
        : products
            .filter((product) =>
              productSearchText(product).includes(normalizeSearchText(query)),
            )
            .slice(0, 7),
    [products, query],
  );
  useEffect(() => {
    if (open)
      setTimeout(() => document.getElementById("global-search")?.focus(), 50);
  }, [open]);
  if (!open) return null;
  return (
    <div
      className="modal-shell"
      role="dialog"
      aria-modal="true"
      aria-label={t.search}
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="search-panel">
        <div className="search-input">
          <Search size={22} />
          <input
            id="global-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t.search}
          />
          <button onClick={onClose} aria-label="Close">
            <X />
          </button>
        </div>
        <div className="search-caption">
          {query
            ? pick(`نتائج البحث عن «${query}»`, `Results for “${query}”`)
            : pick("اقتراحات سريعة", "Quick suggestions")}
        </div>
        <div className="search-results">
          {results.map((product) => (
            <Link
              to={`/product/${product.slug}`}
              onClick={onClose}
              className="search-result"
              key={product.id}
            >
              <ProductImage
                src={product.image}
                alt={`${lang === "ar" ? product.nameAr : product.nameEn} - ${business.businessNameEn}`}
              />
              <span>
                <b>{lang === "ar" ? product.nameAr : product.nameEn}</b>
                <small>
                  {[
                    product.brand,
                    pick(
                      categoryBySlug(product.category)?.ar ||
                        product.categoryAr,
                      categoryBySlug(product.category)?.en || product.category,
                    ),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </small>
              </span>
              <span className="price">{t.askPrice}</span>
            </Link>
          ))}
          {!results.length && <div className="empty-inline">{t.noResults}</div>}
        </div>
        <Link
          to={`/shop${query ? `?q=${encodeURIComponent(query)}` : ""}`}
          className="search-all"
          onClick={onClose}
        >
          {pick("عرض كل النتائج", "View all results")} <ArrowLeft size={16} />
        </Link>
      </div>
    </div>
  );
}

function Header() {
  const { lang, setLang, theme, toggleTheme, cartCount, wishlist } = useStore();
  const { t, pick } = useText();
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const handleSearchShortcut = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
      }
      if (event.key === "Escape") {
        setSearchOpen(false);
        setMenuOpen(false);
      }
    };
    window.addEventListener("keydown", handleSearchShortcut);
    return () => window.removeEventListener("keydown", handleSearchShortcut);
  }, []);
  const nav = [
    ["/", t.home],
    ["/shop", t.shop],
    ["/categories", t.categories],
    ["/supplements", t.supplements],
    ["/beauty-care", t.beauty],
    ["/mother-baby", t.mother],
    ["/about", pick("من نحن", "About Us")],
    ["/contact", pick("تواصل معنا", "Contact Us")],
  ];
  return (
    <>
      <div className="announcement">
        <MessageCircle size={15} />
        <span>
          {pick(
            "اسألنا عن توفر المنتجات عبر واتساب",
            "Ask us about product availability on WhatsApp",
          )}
        </span>
        <a
          href={whatsappUrl(
            pick(
              `مرحبًا ${business.businessNameAr}، أود الاستفسار عن توفر منتج.`,
              `Hello ${business.businessNameEn}, I would like to ask about product availability.`,
            ),
          )}
          target="_blank"
          rel="noreferrer noopener"
        >
          {pick("تواصل الآن", "Contact us")}
        </a>
      </div>
      <header className="site-header">
        <div className="header-main container">
          <button
            className="icon-button menu-trigger"
            onClick={() => setMenuOpen(true)}
            aria-label={t.menu}
          >
            <Menu />
          </button>
          <Logo />
          <button className="header-search" onClick={() => setSearchOpen(true)}>
            <Search size={19} />
            <span>{t.search}</span>
            <kbd>⌘ K</kbd>
          </button>
          <div className="header-actions">
            <button
              className="text-action"
              onClick={() => setLang(lang === "ar" ? "en" : "ar")}
            >
              <Languages size={19} />
              <span>{lang === "ar" ? "EN" : "العربية"}</span>
            </button>
            <button
              className="icon-button"
              onClick={toggleTheme}
              aria-label="Theme"
            >
              {theme === "light" ? <Moon /> : <Sun />}
            </button>
            <Link
              className="icon-button badge-button"
              to="/wishlist"
              aria-label={t.wishlist}
            >
              <Heart />
              <em>{wishlist.length}</em>
            </Link>
            <Link
              className="icon-button badge-button"
              to="/cart"
              aria-label={t.cart}
            >
              <ShoppingBag />
              <em>{cartCount}</em>
            </Link>
            <Link
              className="icon-button account-action"
              to="/account"
              aria-label={t.account}
            >
              <CircleUserRound />
            </Link>
          </div>
        </div>
        <nav className="desktop-nav container" aria-label="Primary navigation">
          {nav.map(([path, label]) => (
            <NavLink key={path} to={path} end={path === "/"}>
              {label}
            </NavLink>
          ))}
        </nav>
      </header>
      <SearchPanel open={searchOpen} onClose={() => setSearchOpen(false)} />
      {menuOpen && (
        <div
          className="drawer-backdrop"
          onMouseDown={(e) =>
            e.target === e.currentTarget && setMenuOpen(false)
          }
        >
          <aside className="menu-drawer">
            <div className="drawer-head">
              <Logo />
              <button
                className="icon-button"
                onClick={() => setMenuOpen(false)}
              >
                <X />
              </button>
            </div>
            <nav>
              {nav.map(([path, label]) => (
                <NavLink
                  onClick={() => setMenuOpen(false)}
                  key={path}
                  to={path}
                >
                  {label}
                  <ChevronLeft size={18} />
                </NavLink>
              ))}
            </nav>
            <div className="drawer-settings">
              <button onClick={() => setLang(lang === "ar" ? "en" : "ar")}>
                <Languages /> {lang === "ar" ? "English" : "العربية"}
              </button>
              <button onClick={toggleTheme}>
                {theme === "light" ? <Moon /> : <Sun />}{" "}
                {pick("تغيير المظهر", "Switch theme")}
              </button>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}

function MobileNav() {
  const { cartCount, wishlist } = useStore();
  const { t } = useText();
  return (
    <nav className="mobile-nav" aria-label="Mobile navigation">
      <NavLink to="/">
        <span>
          <Grid2X2 />
        </span>
        <small>{t.home}</small>
      </NavLink>
      <NavLink to="/categories">
        <span>
          <Menu />
        </span>
        <small>{t.categories}</small>
      </NavLink>
      <NavLink to="/shop">
        <span>
          <Search />
        </span>
        <small>{t.search.split("،")[0].split(" ")[0]}</small>
      </NavLink>
      <NavLink to="/wishlist">
        <span>
          <Heart />
          <em>{wishlist.length}</em>
        </span>
        <small>{t.wishlist}</small>
      </NavLink>
      <NavLink to="/cart">
        <span>
          <ShoppingBag />
          <em>{cartCount}</em>
        </span>
        <small>{t.cart}</small>
      </NavLink>
    </nav>
  );
}

function Footer() {
  const { pick, isAr } = useText();
  return (
    <footer className="footer">
      <div className="container footer-grid">
        <div className="footer-brand">
          <OfficialLogo className="footer-official-logo" />
          <b>MARKA PHARMACY</b>
          <span>{business.businessNameAr}</span>
          <p>
            {pick(
              "تجربة صيدلية رقمية تجمع الصحة والعناية واحتياجات العائلة في مكان واحد.",
              "A digital pharmacy experience bringing health, care and family essentials together.",
            )}
          </p>
        </div>
        <div className="footer-group">
          <h4>{pick("التواصل", "Contact")}</h4>
          <div className="footer-data-list">
            <a href={businessLinks.phone}>
              <small>{pick("الهاتف", "Phone")}</small>
              <b dir="ltr">{business.phoneDisplay}</b>
            </a>
            <a
              href={businessLinks.whatsapp}
              target="_blank"
              rel="noreferrer noopener"
            >
              <small>{pick("واتساب", "WhatsApp")}</small>
              <b dir="ltr">{business.phoneDisplay}</b>
            </a>
            <a href={businessLinks.email}>
              <small>{pick("البريد الإلكتروني", "Email")}</small>
              <b>{business.email}</b>
            </a>
          </div>
        </div>
        <div className="footer-group">
          <h4>{pick("الموقع", "Location")}</h4>
          <p className="footer-address">
            {(isAr ? business.addressLinesAr : business.addressLinesEn).map(
              (line) => (
                <span key={line}>{line}</span>
              ),
            )}
          </p>
          <a
            className="footer-direction-link"
            href={business.mapsUrl}
            target="_blank"
            rel="noreferrer noopener"
          >
            <MapPin /> {pick("الاتجاهات", "Directions")}
          </a>
        </div>
        <div className="footer-group">
          <h4>{pick("ساعات العمل", "Working hours")}</h4>
          <span>{pick(business.workingDaysAr, business.workingDaysEn)}</span>
          <b>{pick(business.workingTimeAr, business.workingTimeEn)}</b>
          <Link to="/prescription">
            {pick("أرسل وصفتك", "Send prescription")}
          </Link>
        </div>
        <div className="footer-group">
          <h4>{pick("وسائل التواصل الاجتماعي", "Social media")}</h4>
          <SocialLinks className="footer-social-links" />
        </div>
      </div>
      <nav
        className="container footer-public-nav"
        aria-label={pick("روابط الموقع", "Footer navigation")}
      >
        <Link to="/shop">{pick("المنتجات", "Products")}</Link>
        <Link to="/categories">{pick("الأقسام", "Categories")}</Link>
        <Link to="/supplements">{pick("المكملات", "Supplements")}</Link>
        <Link to="/beauty-care">
          {pick("العناية والجمال", "Beauty & Care")}
        </Link>
        <Link to="/mother-baby">{pick("الأم والطفل", "Mother & Baby")}</Link>
        <Link to="/about">{pick("من نحن", "About")}</Link>
        <Link to="/contact">{pick("تواصل معنا", "Contact")}</Link>
      </nav>
      <div className="container footer-bottom">
        <span>
          © {new Date().getFullYear()} {business.businessNameEn}
        </span>
        <span>
          {pick(
            "الخصوصية · الشروط · سياسة الاستبدال",
            "Privacy · Terms · Returns",
          )}
        </span>
      </div>
    </footer>
  );
}

function WhatsAppButton() {
  const { pick } = useText();
  return (
    <a
      className="whatsapp-float"
      href={whatsappUrl(
        pick(
          `مرحبًا ${business.businessNameAr}، أود الاستفسار عن توفر منتج.`,
          `Hello ${business.businessNameEn}, I would like to ask about product availability.`,
        ),
      )}
      target="_blank"
      rel="noreferrer noopener"
      aria-label={pick(
        "تواصل مع صيدلية ماركا عبر واتساب",
        "Contact Marka Pharmacy on WhatsApp",
      )}
    >
      <MessageCircle />
      <span>{pick("مساعدة مباشرة", "Live support")}</span>
    </a>
  );
}

function Breadcrumbs({ items }: { items: { label: string; to?: string }[] }) {
  const { t } = useText();
  return (
    <nav className="breadcrumbs" aria-label="Breadcrumb">
      <Link to="/">{t.home}</Link>
      {items.map((item, index) => (
        <span key={index}>
          {index % 2 ? null : null}
          <ChevronLeft size={13} />
          {item.to ? (
            <Link to={item.to}>{item.label}</Link>
          ) : (
            <b>{item.label}</b>
          )}
        </span>
      ))}
    </nav>
  );
}

function SectionHead({
  eyebrow,
  title,
  copyText,
  to,
}: {
  eyebrow?: string;
  title: string;
  copyText?: string;
  to?: string;
}) {
  const { t } = useText();
  return (
    <div className="section-head">
      <div>
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <h2>{title}</h2>
        {copyText && <p>{copyText}</p>}
      </div>
      {to && (
        <Link to={to} className="text-link">
          {t.viewAll}
          <ArrowLeft size={17} />
        </Link>
      )}
    </div>
  );
}

function Price() {
  const { t } = useText();
  return <span className="price ask-price">{t.askPrice}</span>;
}

function ProductCard({ product }: { product: Product }) {
  const { lang, wishlist, toggleWishlist, addToCart } = useStore();
  const { t, pick } = useText();
  const [added, setAdded] = useState(false);
  const add = () => {
    addToCart(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };
  const availabilityLabel =
    product.stockStatus === "in-stock"
      ? t.available
      : product.stockStatus === "out-of-stock"
        ? t.unavailable
        : t.confirmAvailability;
  return (
    <article className="product-card">
      <div className="product-image-wrap">
        <Link to={`/product/${product.slug}`}>
          <ProductImage
            src={product.image}
            alt={`${lang === "ar" ? product.nameAr : product.nameEn} - ${business.businessNameEn}`}
            loading="lazy"
          />
        </Link>
        <button
          className={`heart-button ${wishlist.includes(product.id) ? "active" : ""}`}
          onClick={() => toggleWishlist(product.id)}
          aria-label={t.wishlist}
        >
          <Heart
            fill={wishlist.includes(product.id) ? "currentColor" : "none"}
          />
        </button>
        {product.featured && (
          <span className="product-badge">{pick("مختار", "Featured")}</span>
        )}
      </div>
      <div className="product-info">
        <span className="product-meta">
          {[
            product.brand,
            pick(
              categoryBySlug(product.category)?.ar || product.categoryAr,
              categoryBySlug(product.category)?.en || product.category,
            ),
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
        <Link to={`/product/${product.slug}`}>
          <h3>{lang === "ar" ? product.nameAr : product.nameEn}</h3>
        </Link>
        {lang === "ar" && <span className="product-en">{product.nameEn}</span>}
        <div className="product-status">
          <span
            className={
              product.stockStatus === "in-stock"
                ? "in-stock"
                : product.stockStatus === "out-of-stock"
                  ? "out-stock"
                  : "inquiry-stock"
            }
          >
            {availabilityLabel}
          </span>
          <Price />
        </div>
        <div className="product-actions">
          <button
            className="add-button"
            onClick={add}
            disabled={product.stockStatus === "out-of-stock"}
          >
            {added ? <BadgeCheck /> : <ShoppingCart />}
            <span>{added ? pick("تمت الإضافة", "Added") : t.add}</span>
          </button>
          <a
            href={whatsappUrl(
              productInquiryMessage(
                lang === "ar" ? product.nameAr : product.nameEn,
                lang,
              ),
            )}
            className="wa-mini"
            target="_blank"
            rel="noreferrer noopener"
            aria-label={`${t.inquire}: ${lang === "ar" ? product.nameAr : product.nameEn}`}
          >
            <MessageCircle />
            <span>{t.askPrice}</span>
          </a>
        </div>
      </div>
    </article>
  );
}

function ProductGrid({
  products,
  limit,
}: {
  products: Product[];
  limit?: number;
}) {
  const visible = limit ? products.slice(0, limit) : products;
  return (
    <div className="product-grid">
      {visible.map((product) => (
        <ProductCard key={product.id} product={product} />
      ))}
    </div>
  );
}

function Home() {
  const { products, loading } = useStore();
  const { pick, isAr } = useText();
  const categoryImages = primaryCategories.map(
    (category) =>
      products.filter((p) => p.category === category.slug)[0]?.image ||
      "/assets/product-placeholder.svg",
  );
  return (
    <main>
      <PageMeta
        title={pick(business.businessNameAr, "Home")}
        description={pick(
          "كل ما تحتاجه لصحتك وعنايتك واحتياجات عائلتك، مع خدمة مباشرة من صيدلية ماركا الجنوبية.",
          "Everything you need for health, personal care and family essentials with direct support from Marka Pharmacy.",
        )}
      />
      <section className="hero">
        <div className="container hero-layout">
          <div className="hero-content">
            <span className="hero-kicker">
              <span></span>
              {pick(business.businessNameAr, business.businessNameEn)}
            </span>
            <h1>
              {pick("صحتك وعنايتك...", "Health & Care,")}
              <br />
              <em>{pick("أقرب إليك", "Closer to You")}</em>
            </h1>
            <p>
              {pick(
                "كل ما تحتاجه لصحتك وعنايتك واحتياجات عائلتك، مع خدمة مباشرة من صيدلية ماركا الجنوبية.",
                "Everything you need for your health, personal care and family essentials with direct support from Marka Pharmacy.",
              )}
            </p>
            <div className="hero-actions">
              <Link to="/shop" className="button primary">
                {pick("استكشف المنتجات", "Explore Products")}
                {isAr ? <ArrowLeft /> : <ArrowRight />}
              </Link>
              <a
                href={whatsappUrl(
                  pick(
                    `مرحبًا ${business.businessNameAr}، أود الاستفسار عن توفر منتج.`,
                    `Hello ${business.businessNameEn}, I would like to ask about product availability.`,
                  ),
                )}
                target="_blank"
                rel="noreferrer noopener"
                className="button whatsapp"
              >
                <MessageCircle />
                {pick("تواصل عبر واتساب", "WhatsApp Us")}
              </a>
              <Link to="/prescription" className="button secondary">
                <FileText /> {pick("أرسل وصفتك", "Send Prescription")}
              </Link>
            </div>
            <div className="hero-proof">
              <span>
                <PackageCheck />
                {pick("منتجات متنوعة", "Product variety")}
              </span>
              <span>
                <Clock3 />
                {pick(business.workingDaysAr, business.workingDaysEn)}
              </span>
              <span>
                <Headphones />
                {pick("دعم مباشر", "Direct support")}
              </span>
            </div>
          </div>
          <div className="hero-media">
            <img
              src={business.assets.storefront}
              alt={pick(
                "واجهة صيدلية ماركا الجنوبية في عمّان",
                "Marka Pharmacy storefront in Amman",
              )}
            />
            <div className="hero-media-caption">
              <MapPin />
              <span>
                <b>{pick("ماركا الجنوبية", "South Marka")}</b>
                <small>{pick("شارع الفاو · عمّان", "Al Faw · Amman")}</small>
              </span>
            </div>
          </div>
        </div>
      </section>

      <section
        className="quick-categories container"
        aria-label="Quick categories"
      >
        {primaryCategories.map((category, index) => (
          <Link
            to={`/${category.slug}`}
            className={`quick-category ${category.tone}`}
            key={category.slug}
          >
            <div>
              <category.Icon />
              <span>{pick(category.ar, category.en)}</span>
              <small>{pick("اكتشف المجموعة", "Explore collection")}</small>
            </div>
            <ProductImage
              src={categoryImages[index]}
              alt={pick(`منتجات ${category.ar}`, `${category.en} products`)}
              loading="lazy"
            />
            <ChevronLeft className="quick-arrow" />
          </Link>
        ))}
      </section>

      <section className="trust-strip container">
        {[
          [PackageCheck, "منتجات متنوعة", "Wide selection"],
          [ShoppingBag, "سهولة الطلب", "Easy ordering"],
          [Headphones, "دعم مباشر", "Direct support"],
          [ShieldCheck, "تجربة موثوقة", "Trusted experience"],
        ].map(([Icon, ar, en]) => {
          const C = Icon as typeof ShieldCheck;
          return (
            <div key={String(ar)}>
              <C />
              <span>
                <b>{pick(String(ar), String(en))}</b>
                <small>
                  {pick("تسوق بوضوح وراحة", "Shop with confidence")}
                </small>
              </span>
            </div>
          );
        })}
      </section>

      <section className="section container category-section">
        <SectionHead
          eyebrow={pick("تسوق حسب احتياجك", "Shop your way")}
          title={pick(
            "كل ما تحتاجه، في رف واحد",
            "Everything you need, on one shelf",
          )}
          copyText={pick(
            "نظام أقسام قابل للتوسع ومصمم ليصل بك إلى المنتج بسهولة.",
            "A scalable category system designed to get you to the right product quickly.",
          )}
          to="/categories"
        />
        <div className="shelf-track">
          {categoryTree.map((group, i) => (
            <Link
              to={`/category/${group.slug}`}
              className="shelf-card"
              key={group.slug}
            >
              <span className="shelf-number">0{i + 1}</span>
              <div className="shelf-icon">
                {i === 0 ? (
                  <Zap />
                ) : i === 1 ? (
                  <Sparkles />
                ) : i === 2 ? (
                  <Baby />
                ) : i === 3 ? (
                  <Stethoscope />
                ) : (
                  <ShieldCheck />
                )}
              </div>
              <h3>{pick(group.ar, group.en)}</h3>
              <p>
                {group.subcategories
                  .slice(0, 4)
                  .map((item) => pick(item.ar, item.en))
                  .join(" · ")}
              </p>
              <span className="shelf-link">
                {pick("استكشف القسم", "Explore section")}
                <ChevronLeft />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="section surface-section">
        <div className="container">
          <SectionHead
            eyebrow={pick("مختارات ماركا", "Marka edit")}
            title={pick(
              "منتجات تستحق مكانًا في روتينك",
              "Considered essentials for your routine",
            )}
            to="/shop"
          />
          {loading ? (
            <ProductSkeleton />
          ) : (
            <ProductGrid
              products={products.filter((p) => p.featured)}
              limit={8}
            />
          )}
        </div>
      </section>

      <Spotlight
        slug="supplements"
        eyebrow={pick("صحتك اليومية", "Everyday wellness")}
        title={pick("مكملات لصحتك اليومية", "Everyday wellness supplements")}
        text={pick(
          "فيتامينات، معادن وأوميغا ضمن كتالوج منظم يسهل استكشافه.",
          "Explore vitamins, minerals and omega products in a thoughtfully organized catalog.",
        )}
        products={products
          .filter((p) => p.category === "supplements")
          .slice(0, 4)}
        variant="burgundy"
      />
      <Spotlight
        slug="beauty-care"
        eyebrow={pick("عناية مدروسة", "Care, considered")}
        title={pick("العناية تبدأ من التفاصيل", "Everyday care essentials")}
        text={pick(
          "اختيارات للعناية بالبشرة والشعر والجسم بروح صيدلية احترافية.",
          "Dermocosmetic, hair and body care with a professional pharmacy point of view.",
        )}
        products={products
          .filter((p) => p.category === "beauty-care")
          .slice(0, 4)}
        variant="cream"
      />

      <section className="section container dual-editorial">
        <Link to="/mother-baby" className="editorial-card mother">
          <div>
            <span>
              {pick("رعاية هادئة لكل مرحلة", "Calm care for every stage")}
            </span>
            <h2>{pick("الأم والطفل", "Mother & Baby")}</h2>
            <p>
              {pick(
                "احتياجات التغذية، العناية اليومية والأمومة في مساحة دافئة ومنظمة.",
                "Feeding, daily care and motherhood essentials in one calm space.",
              )}
            </p>
            <b>
              {pick("تسوق القسم", "Shop the edit")}
              <ArrowLeft />
            </b>
          </div>
          <Baby />
        </Link>
        <Link to="/medical-health" className="editorial-card medical">
          <div>
            <span>{pick("الصحة في المنزل", "Health at home")}</span>
            <h2>{pick("أجهزة ومستلزمات مختارة", "Home-health essentials")}</h2>
            <p>
              {pick(
                "حلول متابعة الصحة والعناية اليومية ضمن تجربة نظيفة وواضحة.",
                "Monitoring and everyday medical-care solutions in a clear experience.",
              )}
            </p>
            <b>
              {pick("تسوق القسم", "Shop the edit")}
              <ArrowLeft />
            </b>
          </div>
          <Stethoscope />
        </Link>
      </section>

      <section className="section offers-band prescription-band">
        <div className="container offers-layout">
          <div>
            <span className="eyebrow">
              {pick("خدمة الوصفات", "Prescription inquiry")}
            </span>
            <h2>
              {pick(
                "أرسل وصفتك الطبية بأمان",
                "Prepare your prescription inquiry",
              )}
            </h2>
            <p>
              {pick(
                "اختر صورة أو ملف PDF على جهازك، ثم تواصل مع الصيدلية عبر واتساب. لا يتم رفع الملف إلى الموقع.",
                "Select an image or PDF on your device, then continue with the pharmacy on WhatsApp. The file is not uploaded to this website.",
              )}
            </p>
            <Link to="/prescription" className="button gold">
              {pick("إرسال وصفة طبية", "Send your prescription")}
            </Link>
          </div>
          <div className="offer-stamp">
            <span>MARKA</span>
            <b>{pick("وصفة", "RX")}</b>
            <small>{pick("استفسار مباشر", "DIRECT INQUIRY")}</small>
          </div>
        </div>
      </section>

      <section className="section container">
        <SectionHead
          eyebrow={pick("الأكثر طلبًا", "Community favourites")}
          title={pick("منتجات يتكرر اختيارها", "Best sellers")}
          to="/shop?sort=popular"
        />
        {loading ? (
          <ProductSkeleton />
        ) : (
          <ProductGrid
            products={products.filter((p) => p.bestSeller)}
            limit={4}
          />
        )}
      </section>

      <section className="section why-section">
        <div className="container why-grid">
          <div className="why-copy">
            <span className="eyebrow">
              {pick("لماذا Marka Pharmacy؟", "Why Marka Pharmacy")}
            </span>
            <h2>
              {pick(
                "صيدلية حديثة، بلمسة إنسانية",
                "A modern pharmacy, with a human touch",
              )}
            </h2>
            <p>
              {pick(
                "نبني تجربة تسوق واضحة تجمع تنوع المنتجات، سهولة الوصول والدعم المباشر — دون ادعاءات أو معلومات غير موثقة.",
                "We bring together product variety, easy discovery and direct support—without unverified claims or invented health information.",
              )}
            </p>
            <Link to="/about" className="text-link">
              {pick("تعرف علينا أكثر", "Learn more about us")}
              <ArrowLeft />
            </Link>
          </div>
          <div className="why-cards">
            {[
              [BadgeCheck, "معلومات مسؤولة", "Responsible information"],
              [Search, "وصول أسرع", "Faster discovery"],
              [Languages, "عربي وإنجليزي", "Arabic & English"],
              [Moon, "فاتح وداكن", "Light & dark"],
            ].map(([Icon, ar, en]) => {
              const C = Icon as typeof Search;
              return (
                <div key={String(ar)}>
                  <C />
                  <b>{pick(String(ar), String(en))}</b>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <SupportAndGuide />
    </main>
  );
}

function ProductSkeleton() {
  return (
    <div className="product-grid">
      {Array.from({ length: 4 }).map((_, i) => (
        <div className="skeleton-card" key={i}>
          <span />
          <i />
          <i />
        </div>
      ))}
    </div>
  );
}

function Spotlight({
  slug,
  eyebrow,
  title,
  text,
  products,
  variant,
}: {
  slug: string;
  eyebrow: string;
  title: string;
  text: string;
  products: Product[];
  variant: string;
}) {
  const { pick } = useText();
  return (
    <section className={`section spotlight ${variant}`}>
      <div className="container spotlight-grid">
        <div className="spotlight-copy">
          <span className="eyebrow">{eyebrow}</span>
          <h2>{title}</h2>
          <p>{text}</p>
          <Link to={`/${slug}`} className="button secondary">
            {pick("اكتشف المجموعة", "Explore collection")}
            <ArrowLeft />
          </Link>
          <div className="spotlight-rule">
            <span>MARKA PHARMACY</span>
            <span>CURATED SHELF</span>
          </div>
        </div>
        <div className="mini-product-grid">
          {products.map((product) => (
            <ProductCard product={product} key={product.id} />
          ))}
        </div>
      </div>
    </section>
  );
}

function SupportAndGuide() {
  const { pick, isAr } = useText();
  const faqs = [
    [
      "هل يمكنني الاستفسار عن توفر منتج؟",
      "Can I ask about product availability?",
    ],
    ["هل يمكن الطلب عبر واتساب؟", "Can I order through WhatsApp?"],
    ["هل يوجد توصيل؟", "Is delivery available?"],
    ["هل يمكن إرسال وصفة؟", "Can I send a prescription?"],
  ];
  const [open, setOpen] = useState(0);
  return (
    <>
      <section className="section support-cta">
        <div className="container support-inner">
          <div>
            <MessageCircle />
            <span>
              <small>{pick("نحن قريبون", "We are close")}</small>
              <h2>
                {pick(
                  "اسألنا. نساعدك في العثور على المنتج.",
                  "Ask us. We’ll help you find the product.",
                )}
              </h2>
            </span>
          </div>
          <a
            href={whatsappUrl(
              pick(
                `مرحبًا ${business.businessNameAr}، أود الاستفسار عن توفر منتج.`,
                `Hello ${business.businessNameEn}, I would like to ask about product availability.`,
              ),
            )}
            target="_blank"
            rel="noreferrer noopener"
            className="button light"
          >
            {pick("ابدأ محادثة واتساب", "Start WhatsApp chat")}
            <Send />
          </a>
        </div>
      </section>
      <section className="section container guide-section">
        <SectionHead
          eyebrow={pick("معرفة ببساطة", "Knowledge, made clear")}
          title={pick("دليل الصحة والعناية", "Health & care guide")}
          to="/health-guide"
        />
        <div className="article-grid">
          {[
            ["فهم الفيتامينات", "Understanding vitamins"],
            ["أساسيات الأوميغا", "Omega basics"],
            ["روتين العناية اليومية", "Daily skin care"],
            ["قياس الضغط في المنزل", "Monitoring blood pressure at home"],
          ].map(([ar, en], i) => (
            <Link to="/health-guide" className="article-card" key={ar}>
              <span>0{i + 1}</span>
              <div className={`article-art art-${i}`}>
                <Sparkles />
              </div>
              <small>
                {pick("قراءة عامة · 4 دقائق", "General guide · 4 min")}
              </small>
              <h3>{pick(ar, en)}</h3>
              <p>
                {pick(
                  "معلومات عامة تساعدك على فهم الفئة قبل التسوق، وليست نصيحة طبية.",
                  "General information to understand the category before shopping—not medical advice.",
                )}
              </p>
              <b>
                {pick("اقرأ الدليل", "Read guide")}
                <ArrowLeft />
              </b>
            </Link>
          ))}
        </div>
      </section>
      <section className="section container faq-contact">
        <div>
          <span className="eyebrow">
            {pick("أسئلة شائعة", "Frequently asked")}
          </span>
          <h2>{pick("قبل أن تبدأ", "Before you begin")}</h2>
          <p>
            {pick(
              "إجابات واضحة على أكثر الأسئلة شيوعًا. للتفاصيل المؤكدة، تواصل معنا مباشرة.",
              "Clear answers to common questions. Contact us directly for confirmed details.",
            )}
          </p>
        </div>
        <div className="faq-list">
          {faqs.map(([ar, en], i) => (
            <div className={`faq-item ${open === i ? "open" : ""}`} key={ar}>
              <button onClick={() => setOpen(open === i ? -1 : i)}>
                <span>{pick(ar, en)}</span>
                <Plus />
              </button>
              {open === i && (
                <p>
                  {pick(
                    "نعم، تواصل معنا مباشرة وسنتحقق من التوفر والتفاصيل الحالية قبل إرسال الاستفسار.",
                    "Yes. Contact us and we will confirm current availability and details with your inquiry.",
                  )}
                </p>
              )}
            </div>
          ))}
        </div>
      </section>
      <section className="section container contact-location-section">
        <div className="contact-location-grid">
          <div className="contact-location-copy">
            <span className="eyebrow">MARKA / AMMAN</span>
            <h2>{pick("زورونا أو تواصلوا معنا", "Visit or contact us")}</h2>
            <div className="contact-detail-block">
              <MapPin />
              <span>
                {(isAr ? business.addressLinesAr : business.addressLinesEn).map(
                  (line) => (
                    <b key={line}>{line}</b>
                  ),
                )}
              </span>
            </div>
            <div className="contact-detail-block">
              <Clock3 />
              <span>
                <small>{pick("ساعات العمل", "Working hours")}</small>
                <b>{pick(business.workingDaysAr, business.workingDaysEn)}</b>
                <b>{pick(business.workingTimeAr, business.workingTimeEn)}</b>
              </span>
            </div>
          </div>
          <div className="contact-preview-actions">
            <a
              className="contact-action-card whatsapp-action"
              href={businessLinks.whatsapp}
              target="_blank"
              rel="noreferrer noopener"
            >
              <MessageCircle />{" "}
              <span>{pick("تواصل عبر واتساب", "WhatsApp Us")}</span>
            </a>
            <a className="contact-action-card" href={businessLinks.phone}>
              <Phone /> <span>{pick("اتصل بنا", "Call us")}</span>
            </a>
            <a
              className="contact-action-card"
              href={businessLinks.maps}
              target="_blank"
              rel="noreferrer noopener"
            >
              <MapPin /> <span>{pick("الاتجاهات", "Directions")}</span>
            </a>
            <a className="contact-action-card" href={businessLinks.email}>
              <Mail /> <span>{pick("البريد الإلكتروني", "Email")}</span>
            </a>
          </div>
        </div>
        <LocationMap className="homepage-map" />
      </section>
    </>
  );
}

function PageHero({
  eyebrow,
  title,
  text,
  children,
}: {
  eyebrow?: string;
  title: string;
  text?: string;
  children?: ReactNode;
}) {
  return (
    <section className="page-hero">
      <div className="container">
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <h1>{title}</h1>
        {text && <p>{text}</p>}
        {children}
      </div>
    </section>
  );
}

function Shop({
  fixedCategory,
  fixedSubcategory,
}: {
  fixedCategory?: string;
  fixedSubcategory?: string;
}) {
  const { products, loading } = useStore();
  const { t, pick } = useText();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [query, setQuery] = useState(params.get("q") || "");
  const [category, setCategory] = useState(fixedCategory || "all");
  const [subcategory, setSubcategory] = useState(
    fixedSubcategory || params.get("subcategory") || "all",
  );
  const [sort, setSort] = useState(params.get("sort") || "newest");
  const [inStockOnly, setInStockOnly] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [limit, setLimit] = useState(16);
  const selectedCategory = categoryBySlug(category);
  const pageCategory = categoryBySlug(fixedCategory);
  useEffect(() => {
    setCategory(fixedCategory || "all");
    setSubcategory(fixedSubcategory || params.get("subcategory") || "all");
    setLimit(16);
  }, [fixedCategory, fixedSubcategory, location.search]);
  const filtered = useMemo(
    () =>
      products
        .filter(
          (p) =>
            (category === "all" || p.category === category) &&
            (subcategory === "all" || p.subcategory === subcategory) &&
            (!inStockOnly || p.stockStatus !== "out-of-stock") &&
            productSearchText(p).includes(normalizeSearchText(query)),
        )
        .sort((a, b) =>
          sort === "popular"
            ? Number(b.bestSeller) - Number(a.bestSeller)
            : sort === "name"
              ? a.nameAr.localeCompare(b.nameAr)
              : sort === "category"
                ? a.category.localeCompare(b.category)
                : Number(b.id) - Number(a.id),
        ),
    [products, query, category, subcategory, inStockOnly, sort],
  );
  const changeCategory = (slug: string) => {
    setCategory(slug);
    setSubcategory("all");
    setLimit(16);
  };
  return (
    <main>
      <PageMeta
        title={pageCategory ? pick(pageCategory.ar, pageCategory.en) : t.shop}
        description={
          pageCategory
            ? pick(pageCategory.descriptionAr, pageCategory.descriptionEn)
            : pick(
                "تصفح منتجات صيدلية ماركا الجنوبية وأرسل استفسارك عبر واتساب.",
                "Browse Marka Pharmacy products and send your inquiry via WhatsApp.",
              )
        }
      />
      <PageHero
        eyebrow={pageCategory ? "MARKA / CATEGORY" : "MARKA / SHOP"}
        title={
          pageCategory
            ? pick(pageCategory.ar, pageCategory.en)
            : pick("كل المنتجات", "All Products")
        }
        text={
          pageCategory
            ? pick(pageCategory.descriptionAr, pageCategory.descriptionEn)
            : pick(
                "تصفح كتالوج صيدلية ماركا الجنوبية. يتم تأكيد السعر والتوفر عند الاستفسار.",
                "Browse the Marka Pharmacy catalog. Price and availability are confirmed when you inquire.",
              )
        }
      >
        {pageCategory && (
          <div className="hero-pills">
            {pageCategory.subcategories.slice(0, 6).map((item) => (
              <button
                type="button"
                key={item.slug}
                className={subcategory === item.slug ? "active" : ""}
                onClick={() => setSubcategory(item.slug)}
              >
                {pick(item.ar, item.en)}
              </button>
            ))}
          </div>
        )}
      </PageHero>
      <div className="container">
        <Breadcrumbs
          items={
            pageCategory
              ? [
                  { label: t.categories, to: "/categories" },
                  { label: pick(pageCategory.ar, pageCategory.en) },
                ]
              : [{ label: t.shop }]
          }
        />
        <div className="shop-toolbar">
          <div className="shop-search">
            <Search />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t.search}
            />
          </div>
          <button
            className="filter-mobile button secondary"
            onClick={() => setShowFilters(!showFilters)}
          >
            <List />
            {pick("الفلاتر", "Filters")}
          </button>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            aria-label="Sort"
          >
            <option value="newest">{pick("الأحدث", "Newest")}</option>
            <option value="popular">{pick("الأكثر طلبًا", "Popular")}</option>
            <option value="name">{pick("الاسم", "Name")}</option>
            <option value="category">{pick("الفئة", "Category")}</option>
          </select>
          <span>
            {filtered.length} {pick("منتج", "products")}
          </span>
        </div>
        <div className="shop-layout">
          <aside className={`filters ${showFilters ? "show" : ""}`}>
            <div className="filter-title">
              <b>{pick("تصفية النتائج", "Filter results")}</b>
              <button onClick={() => setShowFilters(false)}>
                <X />
              </button>
            </div>
            {!fixedCategory && (
              <>
                <label className={category === "all" ? "active" : ""}>
                  <input
                    type="radio"
                    name="cat"
                    checked={category === "all"}
                    onChange={() => changeCategory("all")}
                  />
                  {pick("كل المنتجات", "All Products")}
                  <span>{products.length}</span>
                </label>
                {categories.map((c) => (
                  <label
                    className={category === c.slug ? "active" : ""}
                    key={c.slug}
                  >
                    <input
                      type="radio"
                      name="cat"
                      checked={category === c.slug}
                      onChange={() => changeCategory(c.slug)}
                    />
                    {pick(c.ar, c.en)}
                    <span>
                      {products.filter((p) => p.category === c.slug).length}
                    </span>
                  </label>
                ))}
              </>
            )}
            {selectedCategory && (
              <>
                <hr />
                <b>{pick("الفئات الفرعية", "Subcategories")}</b>
                <label className={subcategory === "all" ? "active" : ""}>
                  <input
                    type="radio"
                    name="subcat"
                    checked={subcategory === "all"}
                    onChange={() => setSubcategory("all")}
                  />
                  {pick("الكل", "All")}
                  <span>
                    {products.filter((p) => p.category === category).length}
                  </span>
                </label>
                {selectedCategory.subcategories.map((item) => (
                  <label
                    className={subcategory === item.slug ? "active" : ""}
                    key={item.slug}
                  >
                    <input
                      type="radio"
                      name="subcat"
                      checked={subcategory === item.slug}
                      onChange={() => setSubcategory(item.slug)}
                    />
                    {pick(item.ar, item.en)}
                    <span>
                      {
                        products.filter(
                          (p) =>
                            p.category === category &&
                            p.subcategory === item.slug,
                        ).length
                      }
                    </span>
                  </label>
                ))}
              </>
            )}
            <hr />
            <b>{pick("التوفر", "Availability")}</b>
            <label>
              <input
                type="checkbox"
                checked={inStockOnly}
                onChange={(event) => setInStockOnly(event.target.checked)}
              />
              {pick("متوفر حاليًا", "In stock")}
            </label>
            <div className="filter-note">
              <ShieldCheck />
              <span>
                {pick(
                  "يتم تأكيد السعر والتوفر عند إرسال الاستفسار.",
                  "Price and availability are confirmed with your inquiry.",
                )}
              </span>
            </div>
          </aside>
          <section className="shop-results">
            {loading ? (
              <ProductSkeleton />
            ) : filtered.length ? (
              <>
                <ProductGrid products={filtered.slice(0, limit)} />
                {limit < filtered.length && (
                  <button
                    className="button secondary load-more"
                    onClick={() => setLimit(limit + 16)}
                  >
                    {t.loadMore}
                    <ChevronDown />
                  </button>
                )}
              </>
            ) : (
              <div className="empty-state">
                <Search />
                <h2>{t.noResults}</h2>
                <p>
                  {pick(
                    "لا توجد منتجات مسجلة ضمن هذا التصنيف حاليًا. جرّب تصنيفًا آخر أو تواصل معنا.",
                    "No products are currently recorded in this category. Try another category or contact us.",
                  )}
                </p>
              </div>
            )}
          </section>
        </div>
      </div>
    </main>
  );
}

function CategoriesPage() {
  const { products } = useStore();
  const { pick } = useText();
  return (
    <main>
      <PageMeta title={pick("الأقسام", "Categories")} />
      <PageHero
        eyebrow="MARKA / CATEGORIES"
        title={pick("تسوّق حسب القسم", "Shop by category")}
        text={pick(
          "خريطة واضحة لكل احتياجات الصحة والعناية والعائلة.",
          "A clear map of health, care and family essentials.",
        )}
      />
      <div className="container category-directory">
        {categoryTree.map((group, i) => (
          <section key={group.slug} className="directory-group">
            <div className="directory-art">
              <span>0{i + 1}</span>
              <ProductImage
                src={
                  products.find((product) => product.category === group.slug)
                    ?.image || "/assets/product-placeholder.svg"
                }
                alt={pick(`منتجات ${group.ar}`, `${group.en} products`)}
              />
            </div>
            <div>
              <h2>{pick(group.ar, group.en)}</h2>
              <p className="directory-description">
                {pick(group.descriptionAr, group.descriptionEn)}
              </p>
              <span className="directory-count">
                {
                  products.filter((product) => product.category === group.slug)
                    .length
                }{" "}
                {pick("منتج", "products")}
              </span>
              <div className="subcategory-list">
                {group.subcategories.map((item) => (
                  <Link
                    to={`/category/${group.slug}?subcategory=${item.slug}`}
                    key={item.slug}
                  >
                    {pick(item.ar, item.en)}
                    <ChevronLeft />
                  </Link>
                ))}
              </div>
              <Link className="button secondary" to={`/category/${group.slug}`}>
                {pick("عرض منتجات القسم", "View category products")}
              </Link>
            </div>
          </section>
        ))}
      </div>
    </main>
  );
}

function ProductPage() {
  const { slug } = useParams();
  const { products, lang, wishlist, toggleWishlist, addToCart } = useStore();
  const { t, pick } = useText();
  const legacyProductAliases: Record<string, string> = {
    "product-8995": "product-9036",
    "product-9030": "product-9029",
    "product-9025": "product-9022",
  };
  const resolvedSlug = (slug && legacyProductAliases[slug]) || slug;
  const product = products.find((p) => p.slug === resolvedSlug);
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState("overview");
  if (!product)
    return (
      <main>
        <PageHero title={pick("جاري تحميل المنتج…", "Loading product…")} />
      </main>
    );
  const name = lang === "ar" ? product.nameAr : product.nameEn;
  const availabilityLabel =
    product.stockStatus === "in-stock"
      ? t.available
      : product.stockStatus === "out-of-stock"
        ? t.unavailable
        : t.confirmAvailability;
  return (
    <main>
      <PageMeta
        title={name}
        description={
          lang === "ar"
            ? product.shortDescriptionAr
            : product.shortDescriptionEn
        }
      />
      <div className="container product-page">
        <Breadcrumbs
          items={[{ label: t.shop, to: "/shop" }, { label: name }]}
        />
        <div className="product-detail">
          <div className="gallery">
            <div className="main-product-image">
              <ProductImage
                src={product.image}
                alt={`${name} - ${business.businessNameEn}`}
              />
            </div>
            <div className="thumbs">
              {[product.image, product.image, product.image].map((img, i) => (
                <button className={i === 0 ? "active" : ""} key={i}>
                  <ProductImage
                    src={img}
                    alt={`${name} - ${business.businessNameEn} ${i + 1}`}
                  />
                </button>
              ))}
            </div>
          </div>
          <div className="product-summary">
            <span className="product-meta">
              {[
                product.brand,
                pick(
                  categoryBySlug(product.category)?.ar || product.categoryAr,
                  categoryBySlug(product.category)?.en || product.category,
                ),
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
            <h1>{name}</h1>
            {lang === "ar" && <h2>{product.nameEn}</h2>}
            <div className="sku">
              SKU: {product.sku} <span>·</span> <b>{availabilityLabel}</b>
            </div>
            <p>
              {lang === "ar"
                ? product.shortDescriptionAr
                : product.shortDescriptionEn}
            </p>
            <Price />
            <div className="product-notice">
              <ShieldCheck />
              <span>
                {pick(
                  "السعر والتوفر يُؤكدان عند إرسال الاستفسار.",
                  "Price and availability are confirmed with your inquiry.",
                )}
              </span>
            </div>
            <div className="buy-row">
              <div className="quantity">
                <button onClick={() => setQty(Math.max(1, qty - 1))}>
                  <Minus />
                </button>
                <span>{qty}</span>
                <button onClick={() => setQty(qty + 1)}>
                  <Plus />
                </button>
              </div>
              <button
                className="button primary grow"
                onClick={() => addToCart(product, qty)}
              >
                <ShoppingCart />
                {t.add}
              </button>
            </div>
            <div className="product-secondary">
              <button
                className="button secondary"
                onClick={() => toggleWishlist(product.id)}
              >
                <Heart
                  fill={wishlist.includes(product.id) ? "currentColor" : "none"}
                />
                {t.wishlist}
              </button>
              <a
                className="button whatsapp"
                href={whatsappUrl(productInquiryMessage(name, lang))}
                target="_blank"
                rel="noreferrer noopener"
              >
                <MessageCircle />
                {t.askPrice}
              </a>
            </div>
            <div className="delivery-lines">
              <span>
                <PackageCheck />
                {pick("طلب سهل ودعم مباشر", "Easy ordering and direct support")}
              </span>
              <span>
                <ShieldCheck />
                {pick(
                  "معلومات المنتج من المصدر الحالي",
                  "Product information from current source",
                )}
              </span>
            </div>
          </div>
        </div>
        <div className="product-tabs">
          <div className="tab-buttons">
            {[
              ["overview", "نظرة عامة", "Overview"],
              ["details", "التفاصيل", "Details"],
              ["delivery", "التوصيل", "Delivery"],
              ["important", "معلومات مهمة", "Important information"],
            ].map(([id, ar, en]) => (
              <button
                className={tab === id ? "active" : ""}
                onClick={() => setTab(id)}
                key={id}
              >
                {pick(ar, en)}
              </button>
            ))}
          </div>
          <div className="tab-content">
            <h2>{pick("معلومات المنتج", "Product information")}</h2>
            <p>
              {tab === "overview"
                ? lang === "ar"
                  ? product.descriptionAr
                  : product.descriptionEn
                : tab === "delivery"
                  ? pick(
                      "تُضاف سياسة التوصيل الرسمية ومناطق الخدمة بعد تأكيدها من إدارة Marka Pharmacy.",
                      "Official delivery policy and service areas will be added after confirmation.",
                    )
                  : pick(
                      "هذه الصفحة لا تقدم جرعات أو تشخيصًا أو ادعاءات علاجية. راجع العبوة ومصدر المنتج الموثّق قبل الاستخدام.",
                      "This page does not provide dosage, diagnosis or treatment claims. Review the pack and verified product source before use.",
                    )}
            </p>
          </div>
        </div>
        <section className="related">
          <SectionHead
            title={pick("قد يعجبك أيضًا", "You may also like")}
            to="/shop"
          />
          <ProductGrid
            products={products
              .filter(
                (p) => p.category === product.category && p.id !== product.id,
              )
              .slice(0, 4)}
          />
        </section>
      </div>
    </main>
  );
}

function WishlistPage() {
  const { products, wishlist } = useStore();
  const { pick } = useText();
  const items = products.filter((p) => wishlist.includes(p.id));
  return (
    <main>
      <PageMeta title={pick("المفضلة", "Wishlist")} />
      <PageHero
        eyebrow="MARKA / SAVED"
        title={pick("قائمة المفضلة", "Your wishlist")}
        text={pick(
          "احتفظ بالمنتجات للعودة إليها لاحقًا.",
          "Save products and return to them later.",
        )}
      />
      <div className="container simple-page">
        {items.length ? (
          <ProductGrid products={items} />
        ) : (
          <Empty
            icon={<Heart />}
            title={pick("قائمتك فارغة", "Your wishlist is empty")}
            text={pick(
              "احفظ المنتجات التي تهمك لتجدها هنا.",
              "Save products you like and find them here.",
            )}
            action="/shop"
            actionText={pick("ابدأ التسوق", "Start shopping")}
          />
        )}
      </div>
    </main>
  );
}

function CartPage() {
  const { cart, updateQuantity, removeFromCart, lang } = useStore();
  const { t, pick } = useText();
  const inquiryUrl = whatsappUrl(
    cartInquiryMessage(
      cart.map(({ product, quantity }) => ({
        name: lang === "ar" ? product.nameAr : product.nameEn,
        quantity,
      })),
      lang,
    ),
  );
  return (
    <main>
      <PageMeta title={t.cart} />
      <PageHero
        eyebrow="MARKA / INQUIRY CART"
        title={pick("سلة الاستفسار", "Inquiry Cart")}
        text={pick(
          "اجمع المنتجات التي تهمك، ثم أرسل قائمة واحدة إلى الصيدلية عبر واتساب.",
          "Collect the products you need, then send one inquiry list to the pharmacy on WhatsApp.",
        )}
      />
      <div className="container cart-layout">
        {cart.length ? (
          <>
            <div className="cart-list">
              {cart.map(({ product, quantity }) => (
                <article className="cart-item" key={product.id}>
                  <ProductImage
                    src={product.image}
                    alt={`${pick(product.nameAr, product.nameEn)} - ${business.businessNameEn}`}
                  />
                  <div>
                    <span className="product-meta">{product.brand}</span>
                    <Link to={`/product/${product.slug}`}>
                      <h3>{pick(product.nameAr, product.nameEn)}</h3>
                    </Link>
                  </div>
                  <div className="quantity">
                    <button
                      onClick={() => updateQuantity(product.id, quantity - 1)}
                    >
                      <Minus />
                    </button>
                    <span>{quantity}</span>
                    <button
                      onClick={() => updateQuantity(product.id, quantity + 1)}
                    >
                      <Plus />
                    </button>
                  </div>
                  <button
                    className="remove"
                    onClick={() => removeFromCart(product.id)}
                  >
                    <Trash2 />
                    <span>{pick("إزالة", "Remove")}</span>
                  </button>
                </article>
              ))}
            </div>
            <aside className="order-card">
              <h2>{pick("ملخص الاستفسار", "Inquiry summary")}</h2>
              <div>
                <span>{pick("عدد القطع", "Items")}</span>
                <b>{cart.reduce((s, i) => s + i.quantity, 0)}</b>
              </div>
              <p>
                {pick(
                  "ستتضمن رسالة واتساب أسماء المنتجات والكميات. تؤكد الصيدلية التوفر والأسعار مباشرة.",
                  "Your WhatsApp message will include product names and quantities. The pharmacy will confirm availability and prices directly.",
                )}
              </p>
              <a
                href={inquiryUrl}
                className="button primary full"
                target="_blank"
                rel="noreferrer noopener"
              >
                <MessageCircle />
                {pick("إرسال الطلب عبر واتساب", "Send Inquiry via WhatsApp")}
              </a>
              <Link className="button secondary full" to="/inquiry">
                {pick("مراجعة طلب الاستفسار", "Review Inquiry")}
                <ArrowLeft />
              </Link>
              <Link className="continue-link" to="/shop">
                {t.continue}
              </Link>
            </aside>
          </>
        ) : (
          <Empty
            icon={<ShoppingBag />}
            title={pick("سلتك فارغة", "Your cart is empty")}
            text={pick(
              "أضف منتجات من الكتالوج لتضمينها في استفسار واتساب واحد.",
              "Add products from the catalog to include them in one WhatsApp inquiry.",
            )}
            action="/shop"
            actionText={pick("تصفح المنتجات", "Browse products")}
          />
        )}
      </div>
    </main>
  );
}

function Empty({
  icon,
  title,
  text,
  action,
  actionText,
}: {
  icon: ReactNode;
  title: string;
  text: string;
  action: string;
  actionText: string;
}) {
  return (
    <div className="empty-state big">
      {icon}
      <h2>{title}</h2>
      <p>{text}</p>
      <Link className="button primary" to={action}>
        {actionText}
      </Link>
    </div>
  );
}

function InquiryPage() {
  const { cart, lang } = useStore();
  const { pick } = useText();
  const [details, setDetails] = useState({ name: "", phone: "", note: "" });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const message = cartInquiryMessage(
      cart.map(({ product, quantity }) => ({
        name: lang === "ar" ? product.nameAr : product.nameEn,
        quantity,
      })),
      lang,
      details,
    );
    window.open(whatsappUrl(message), "_blank", "noopener,noreferrer");
  };
  return (
    <main>
      <PageMeta title={pick("مراجعة طلب الاستفسار", "Review Inquiry")} />
      <PageHero
        eyebrow="MARKA / INQUIRY"
        title={pick("مراجعة طلب الاستفسار", "Review Inquiry")}
        text={pick(
          "أضف بيانات التواصل الاختيارية، راجع المنتجات، ثم افتح رسالة واتساب الجاهزة.",
          "Add your contact details, review the products, then open the prepared WhatsApp message.",
        )}
      />
      {cart.length ? (
        <form className="container checkout-layout" onSubmit={submit}>
          <div className="checkout-form">
            <FormSection title={pick("بيانات التواصل", "Contact details")}>
              <div className="field-grid">
                <label className="field">
                  <span>{pick("الاسم الكامل", "Full name")} *</span>
                  <input
                    value={details.name}
                    onChange={(event) =>
                      setDetails({ ...details, name: event.target.value })
                    }
                    required
                  />
                </label>
                <label className="field">
                  <span>{pick("رقم الهاتف", "Phone")} *</span>
                  <input
                    type="tel"
                    value={details.phone}
                    onChange={(event) =>
                      setDetails({ ...details, phone: event.target.value })
                    }
                    required
                  />
                </label>
              </div>
              <label className="field">
                <span>{pick("ملاحظات اختيارية", "Optional note")}</span>
                <textarea
                  rows={4}
                  value={details.note}
                  onChange={(event) =>
                    setDetails({ ...details, note: event.target.value })
                  }
                />
              </label>
            </FormSection>
          </div>
          <aside className="order-card sticky">
            <h2>{pick("المنتجات المختارة", "Selected products")}</h2>
            {cart.map((item) => (
              <div className="checkout-product" key={item.product.id}>
                <ProductImage
                  src={item.product.image}
                  alt={`${pick(item.product.nameAr, item.product.nameEn)} - ${business.businessNameEn}`}
                />
                <span>
                  <b>{pick(item.product.nameAr, item.product.nameEn)}</b>
                  <small>
                    {pick("الكمية", "Quantity")}: {item.quantity}
                  </small>
                </span>
              </div>
            ))}
            <button className="button primary full" type="submit">
              <MessageCircle />
              {pick("إرسال عبر واتساب", "Send via WhatsApp")}
            </button>
            <small className="safe-note">
              <ShieldCheck />
              {pick(
                "يفتح الزر واتساب فقط؛ لا يتم حفظ بياناتك أو إرسالها إلى خادم الموقع.",
                "This opens WhatsApp only; your details are not stored or sent to the website.",
              )}
            </small>
          </aside>
        </form>
      ) : (
        <div className="container simple-page">
          <Empty
            icon={<ShoppingBag />}
            title={pick("لا توجد منتجات للمراجعة", "No products to review")}
            text={pick(
              "أضف منتجات إلى سلة الاستفسار أولًا.",
              "Add products to your inquiry cart first.",
            )}
            action="/shop"
            actionText={pick("تصفح المنتجات", "Browse products")}
          />
        </div>
      )}
    </main>
  );
}

function FormSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="form-section">
      <h2>{title}</h2>
      {children}
    </section>
  );
}
function Field({
  label,
  type = "text",
  required = false,
}: {
  label: string;
  type?: string;
  required?: boolean;
}) {
  return (
    <label className="field">
      <span>
        {label}
        {required && " *"}
      </span>
      <input type={type} required={required} />
    </label>
  );
}

function AccountPage() {
  const { pick } = useText();
  const [tab, setTab] = useState("login");
  return (
    <main>
      <PageMeta title={pick("حسابي", "My account")} />
      <PageHero
        eyebrow="MARKA / ACCOUNT"
        title={pick("حسابك، عندما تحتاجه", "Your account, when you need it")}
        text={pick(
          "واجهة جاهزة للربط مع نظام الحسابات الحقيقي لاحقًا.",
          "An interface ready for future connection to a real account system.",
        )}
      />
      <div className="container account-layout">
        <div className="account-tabs">
          <button
            className={tab === "login" ? "active" : ""}
            onClick={() => setTab("login")}
          >
            {pick("تسجيل الدخول", "Sign in")}
          </button>
          <button
            className={tab === "signup" ? "active" : ""}
            onClick={() => setTab("signup")}
          >
            {pick("إنشاء حساب", "Create account")}
          </button>
        </div>
        <form className="account-form" onSubmit={(e) => e.preventDefault()}>
          <Logo />
          <h2>
            {tab === "login"
              ? pick("مرحبًا بعودتك", "Welcome back")
              : pick("أنشئ حسابك", "Create your account")}
          </h2>
          {tab === "signup" && (
            <Field label={pick("الاسم الكامل", "Full name")} required />
          )}
          <Field
            label={pick("البريد الإلكتروني", "Email")}
            type="email"
            required
          />
          <Field
            label={pick("كلمة المرور", "Password")}
            type="password"
            required
          />
          <button className="button primary full">
            {tab === "login"
              ? pick("تسجيل الدخول", "Sign in")
              : pick("إنشاء الحساب", "Create account")}
          </button>
          <p>
            {pick(
              "النموذج تجريبي ولن ينشئ حسابًا قبل ربط نظام المصادقة الخلفي.",
              "This prototype will not create an account until backend authentication is connected.",
            )}
          </p>
        </form>
        <div className="account-benefits">
          {[
            [ShoppingBag, "الطلبات", "Orders"],
            [MapPin, "العناوين المحفوظة", "Saved addresses"],
            [Heart, "المفضلة", "Wishlist"],
            [CircleUserRound, "الملف الشخصي", "Profile"],
          ].map(([Icon, ar, en]) => {
            const C = Icon as typeof Heart;
            return (
              <div key={String(ar)}>
                <C />
                <b>{pick(String(ar), String(en))}</b>
                <small>{pick("جاهز للربط", "Integration ready")}</small>
              </div>
            );
          })}
        </div>
      </div>
    </main>
  );
}

function PrescriptionPage() {
  const { lang } = useStore();
  const { pick } = useText();
  const [file, setFile] = useState("");
  const [details, setDetails] = useState({ name: "", phone: "", note: "" });
  const submitPrescription = (event: FormEvent) => {
    event.preventDefault();
    window.open(
      whatsappUrl(prescriptionInquiryMessage(lang, details)),
      "_blank",
      "noopener,noreferrer",
    );
  };
  return (
    <main>
      <PageMeta title={pick("أرسل وصفتك", "Send prescription")} />
      <PageHero
        eyebrow="MARKA / PRESCRIPTION"
        title={pick("أرسل وصفتك الطبية", "Send your prescription")}
        text={pick(
          "جهّز بياناتك وملف الوصفة، ثم تابع إلى واتساب لإرساله مباشرة إلى الصيدلية.",
          "Prepare your details and prescription file, then continue to WhatsApp to send it directly to the pharmacy.",
        )}
      />
      <div className="container prescription-layout">
        <form onSubmit={submitPrescription}>
          <label className="field">
            <span>{pick("الاسم الكامل", "Full name")} *</span>
            <input
              value={details.name}
              onChange={(event) =>
                setDetails({ ...details, name: event.target.value })
              }
              required
            />
          </label>
          <label className="field">
            <span>{pick("رقم الهاتف", "Phone")} *</span>
            <input
              type="tel"
              value={details.phone}
              onChange={(event) =>
                setDetails({ ...details, phone: event.target.value })
              }
              required
            />
          </label>
          <label className="upload-box">
            <input
              type="file"
              accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
              onChange={(e) => setFile(e.target.files?.[0]?.name || "")}
              required
            />
            <UploadCloud />
            <b>{file || pick("ارفع صورة أو ملف PDF", "Upload image or PDF")}</b>
            <small>
              {pick(
                "JPG أو JPEG أو PNG أو PDF · يبقى الملف على جهازك في هذه الخطوة",
                "JPG, JPEG, PNG or PDF · the file stays on your device at this step",
              )}
            </small>
          </label>
          <label className="field">
            <span>{pick("ملاحظات اختيارية", "Optional notes")}</span>
            <textarea
              rows={4}
              value={details.note}
              onChange={(event) =>
                setDetails({ ...details, note: event.target.value })
              }
            />
          </label>
          <button className="button primary full">
            <MessageCircle />
            {pick("المتابعة إلى واتساب", "Continue to WhatsApp")}
          </button>
          <p className="form-disclosure">
            {pick(
              "لا يرفع الموقع الملف أو يخزنه. بعد فتح واتساب، أرفق ملف الوصفة الذي اخترته يدويًا داخل المحادثة.",
              "This website does not upload or store the file. After WhatsApp opens, attach the selected prescription file manually in the chat.",
            )}
          </p>
        </form>
        <aside>
          <ShieldCheck />
          <h2>{pick("خصوصيتك مهمة", "Your privacy matters")}</h2>
          <p>
            {pick(
              "واجهة اختيار الملف جاهزة لربطها مستقبلًا بمساحة تخزين آمنة. حاليًا لا يغادر الملف جهازك عبر هذا الموقع.",
              "The file-selection UI is ready for future secure storage. For now, the file never leaves your device through this website.",
            )}
          </p>
          <hr />
          <MessageCircle />
          <h3>{pick("تفضّل واتساب؟", "Prefer WhatsApp?")}</h3>
          <p>
            {pick(
              "افتح محادثة واتساب الرسمية، ثم أرفق صورة الوصفة أو ملف PDF داخل المحادثة.",
              "Open the official WhatsApp chat, then attach your prescription image or PDF in the conversation.",
            )}
          </p>
          <a
            className="button whatsapp"
            href={whatsappUrl(prescriptionInquiryMessage(lang))}
            target="_blank"
            rel="noreferrer noopener"
          >
            {pick("التواصل عبر واتساب", "Continue on WhatsApp")}
          </a>
        </aside>
      </div>
    </main>
  );
}

function OffersPage() {
  const { pick } = useText();
  return (
    <main>
      <PageMeta title={pick("العروض", "Offers")} />
      <PageHero
        eyebrow="MARKA / OFFERS"
        title={pick("العروض", "Offers")}
        text={pick(
          "هذه الصفحة محفوظة للإعلانات الرسمية عند توفرها.",
          "This page is reserved for verified pharmacy announcements when available.",
        )}
      />
      <div className="container simple-page">
        <div className="empty-state big offers-empty">
          <Sparkles />
          <h2>
            {pick(
              "لا توجد عروض مفعلة حاليًا.",
              "There are currently no active offers.",
            )}
          </h2>
          <p>
            {pick(
              "تابعنا أو تواصل معنا عبر واتساب لمعرفة أحدث المنتجات والعروض عند توفرها.",
              "Follow Marka Pharmacy or contact us on WhatsApp for future promotions and product updates.",
            )}
          </p>
          <a
            className="button primary"
            href={businessLinks.whatsapp}
            target="_blank"
            rel="noreferrer noopener"
          >
            <MessageCircle /> {pick("تواصل عبر واتساب", "Contact on WhatsApp")}
          </a>
        </div>
      </div>
    </main>
  );
}

function AboutPage() {
  const { pick } = useText();
  return (
    <main>
      <PageMeta
        title={pick("من نحن | صيدلية ماركا الجنوبية", "About Us")}
        description={pick(
          "تعرف على صيدلية ماركا الجنوبية والصيدلي المسؤول رائد جمعة في عمّان.",
          "Learn about Marka Pharmacy and Responsible Pharmacist Raed Jomaa in Amman.",
        )}
      />
      <PageHero
        eyebrow="MARKA / ABOUT"
        title={pick("من نحن", "About Marka Pharmacy")}
        text={pick(business.businessNameAr, business.businessNameEn)}
      />
      <section className="container about-intro">
        <div className="about-intro-copy">
          <span className="eyebrow">MARKA / AMMAN</span>
          <h2>
            {pick(
              "من قلب عمّان... عناية صحية أقرب إليك",
              "From the heart of Amman, care that feels closer",
            )}
          </h2>
          <p>
            {pick(
              "في صيدلية ماركا الجنوبية نؤمن بأن الرعاية الصحية تبدأ بالاهتمام والثقة وسهولة الوصول إلى ما تحتاجه. نسعى لتقديم تجربة صيدلية تجمع بين المنتجات الصحية والعناية الشخصية والمكملات واحتياجات العائلة، مع خدمة مباشرة تساعد عملاءنا على الاستفسار عن المنتجات وتوفرها بسهولة.",
              "At Marka Pharmacy, we believe care begins with attention, trust and easy access to what you need. We bring health products, personal care, supplements and family essentials together with direct support that makes it simple to ask about products and availability.",
            )}
          </p>
          <div className="about-facts">
            <div>
              <MapPin />
              <span>
                <small>{pick("الموقع", "Location")}</small>
                <b>{pick(business.addressAr, business.addressEn)}</b>
              </span>
            </div>
            <div>
              <Clock3 />
              <span>
                <small>{pick("ساعات العمل", "Hours")}</small>
                <b>{pick(business.workingHoursAr, business.workingHoursEn)}</b>
              </span>
            </div>
          </div>
        </div>
        <div className="about-logo-panel">
          <OfficialLogo />
        </div>
      </section>

      <section className="section pharmacist-section">
        <div className="container pharmacist-grid">
          <div className="pharmacist-portrait">
            <img
              src={business.assets.pharmacist}
              alt={pick(
                "الصيدلي رائد جمعة في صيدلية ماركا الجنوبية",
                "Pharmacist Raed Jomaa at Marka Pharmacy",
              )}
            />
          </div>
          <div className="pharmacist-copy">
            <span className="eyebrow">
              {pick("الصيدلي المسؤول", "Responsible Pharmacist")}
            </span>
            <h2>
              {pick(business.pharmacistNameAr, business.pharmacistNameEn)}
            </h2>
            <h3>
              {pick(
                `الصيدلي ${business.pharmacistNameAr}`,
                business.responsiblePharmacistEn,
              )}
            </h3>
            <p>
              {pick(
                "نحرص في صيدلية ماركا الجنوبية على أن تكون تجربة العميل واضحة ومريحة، من الاستفسار عن توفر المنتجات والمكملات إلى احتياجات العناية اليومية والأسرة. هدفنا هو تقديم خدمة قريبة من الناس مبنية على الاهتمام، التنظيم وسهولة التواصل.",
                "At Marka Pharmacy, we work to make every customer interaction clear and comfortable—from asking about product and supplement availability to everyday care and family needs. Our goal is approachable service built around attention, organization and easy communication.",
              )}
            </p>
            <a
              className="button whatsapp"
              href={businessLinks.whatsapp}
              target="_blank"
              rel="noreferrer noopener"
            >
              <MessageCircle />{" "}
              {pick("تواصل مع الصيدلية", "Contact the pharmacy")}
            </a>
          </div>
        </div>
      </section>

      <section className="container about-store-section">
        <div className="about-store-image">
          <img
            src={business.assets.storefront}
            alt={pick(
              "واجهة صيدلية ماركا الجنوبية في عمّان",
              "Marka Pharmacy storefront in Amman",
            )}
          />
        </div>
        <div className="about-store-copy">
          <span className="eyebrow">MARKA / COMMUNITY</span>
          <h2>
            {pick(
              "صيدلية قريبة من مجتمعها",
              "A pharmacy close to its community",
            )}
          </h2>
          <p>
            {pick(
              "نستقبل استفسارات المنتجات يوميًا، ونوفر وسيلة مباشرة للوصول إلى الصيدلية عبر الهاتف وواتساب والاتجاهات على Google Maps.",
              "We welcome product inquiries daily and make the pharmacy easy to reach by phone, WhatsApp and Google Maps directions.",
            )}
          </p>
          <div className="about-store-points">
            <span>
              <MapPin /> {pick("ماركا الجنوبية، عمّان", "South Marka, Amman")}
            </span>
            <span>
              <Clock3 />{" "}
              {pick(business.workingHoursAr, business.workingHoursEn)}
            </span>
            <span>
              <MessageCircle />{" "}
              {pick("دعم مباشر عبر واتساب", "Direct WhatsApp support")}
            </span>
            <span>
              <PackageCheck />{" "}
              {pick(
                "منتجات للصحة والعناية والعائلة",
                "Health, care and family products",
              )}
            </span>
          </div>
          <a
            className="button secondary"
            href={business.mapsUrl}
            target="_blank"
            rel="noreferrer noopener"
          >
            <MapPin /> {pick("الاتجاهات", "Get Directions")}
          </a>
        </div>
      </section>
    </main>
  );
}

function ContactPage() {
  const { pick } = useText();
  return (
    <main>
      <PageMeta
        title={pick("تواصل معنا | صيدلية ماركا الجنوبية", "Contact Us")}
        description={pick(
          "تواصل مع صيدلية ماركا الجنوبية للاستفسار عن المنتجات، إرسال الوصفة والوصول إلى الصيدلية.",
          "Contact Marka Pharmacy for product inquiries, prescription support and directions to the pharmacy.",
        )}
      />
      <PageHero
        eyebrow="MARKA / CONTACT"
        title={pick("تواصل مع صيدلية ماركا الجنوبية", "Contact Marka Pharmacy")}
        text={pick(
          "نحن هنا لمساعدتك في الاستفسار عن المنتجات، إرسال الوصفة والوصول إلى الصيدلية بسهولة.",
          "We are here to help with product inquiries, prescription support and easy directions to the pharmacy.",
        )}
      />
      <div className="container contact-page">
        <div className="contact-cards">
          <a href={businessLinks.phone} className="contact-card">
            <Phone />
            <span>
              <small>{pick("الهاتف", "Phone")}</small>
              <b dir="ltr">{business.phoneDisplay}</b>
            </span>
          </a>
          <a
            href={businessLinks.whatsapp}
            className="contact-card"
            target="_blank"
            rel="noreferrer noopener"
          >
            <MessageCircle />
            <span>
              <small>WhatsApp</small>
              <b dir="ltr">{business.phoneDisplay}</b>
            </span>
          </a>
          <a href={businessLinks.email} className="contact-card">
            <Mail />
            <span>
              <small>{pick("البريد الإلكتروني", "Email")}</small>
              <b>{business.email}</b>
            </span>
          </a>
          <a
            href={business.mapsUrl}
            className="contact-card"
            target="_blank"
            rel="noreferrer noopener"
          >
            <MapPin />
            <span>
              <small>{pick("العنوان", "Address")}</small>
              <b>{pick(business.addressAr, business.addressEn)}</b>
            </span>
          </a>
          <div className="contact-card">
            <Clock3 />
            <span>
              <small>{pick("ساعات العمل", "Working Hours")}</small>
              <b>{pick(business.workingDaysAr, business.workingDaysEn)}</b>
              <b>{pick(business.workingTimeAr, business.workingTimeEn)}</b>
            </span>
          </div>
        </div>
        <section className="contact-map-section">
          <div className="section-head compact">
            <div>
              <span className="eyebrow">MARKA / LOCATION</span>
              <h2>{pick("موقع الصيدلية", "Find the pharmacy")}</h2>
              <p>{pick(business.addressAr, business.addressEn)}</p>
            </div>
          </div>
          <LocationMap className="contact-page-map" />
        </section>
        <div className="contact-lower-grid">
          <section className="contact-social-section">
            <OfficialLogo className="contact-official-logo" />
            <div>
              <span className="eyebrow">MARKA / SOCIAL</span>
              <h2>
                {pick("تابع صيدلية ماركا الجنوبية", "Follow Marka Pharmacy")}
              </h2>
              <SocialLinks />
            </div>
          </section>
          <section className="contact-prescription-cta">
            <FileText />
            <div>
              <span className="eyebrow">MARKA / PRESCRIPTION</span>
              <h2>{pick("أرسل وصفتك الطبية", "Send your prescription")}</h2>
              <p>
                {pick(
                  "يمكنك إرسال وصفتك والتواصل معنا للاستفسار عن توفر المنتجات.",
                  "Send your prescription and contact us to ask about product availability.",
                )}
              </p>
              <Link className="button primary" to="/prescription">
                <FileText /> {pick("إرسال الوصفة", "Send Prescription")}
              </Link>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}

function HealthGuidePage() {
  const { pick } = useText();
  const articles = [
    ["فهم الفيتامينات", "Understanding Vitamins"],
    ["أساسيات الأوميغا", "Omega Basics"],
    ["العناية اليومية بالبشرة", "Daily Skin Care"],
    ["روتين العناية بالشعر", "Hair Care Routine"],
    ["أساسيات الأم والطفل", "Mother & Baby Essentials"],
    ["متابعة ضغط الدم في المنزل", "Monitoring Blood Pressure at Home"],
    ["العناية العامة بالفم", "General Oral Care"],
    ["دليل تسوق المكملات", "Supplement Shopping Guide"],
  ];
  return (
    <main>
      <PageMeta title={pick("دليل الصحة", "Health Guide")} />
      <PageHero
        eyebrow="MARKA / HEALTH GUIDE"
        title={pick(
          "معلومات أوضح لاختيارات يومية",
          "Clearer information for everyday choices",
        )}
        text={pick(
          "محتوى تثقيفي عام لا يقدم تشخيصًا أو علاجًا أو بديلًا عن الاستشارة المهنية.",
          "General educational content—not diagnosis, treatment or a substitute for professional advice.",
        )}
      />
      <div className="container guide-list">
        {articles.map(([ar, en], i) => (
          <article key={ar}>
            <div className={`guide-art art-${i % 4}`}>
              <span>0{i + 1}</span>
              <Sparkles />
            </div>
            <div>
              <span className="eyebrow">
                {pick("دليل عام", "General guide")}
              </span>
              <h2>{pick(ar, en)}</h2>
              <p>
                {pick(
                  "مقدمة مبسطة تساعدك على فهم الفئة والأسئلة المناسبة التي يمكنك طرحها قبل اختيار المنتج. سيتم اعتماد المحتوى النهائي من مصادر موثوقة قبل النشر.",
                  "A simple introduction to understand the category and questions to ask before choosing a product. Final content should be reviewed against trusted sources before publishing.",
                )}
              </p>
              <button className="text-link">
                {pick("اقرأ قريبًا", "Coming soon")}
                <ArrowLeft />
              </button>
            </div>
          </article>
        ))}
      </div>
    </main>
  );
}

function ShopCategoryRoute() {
  const { slug = "supplements" } = useParams();
  if (slug === "medical-health") {
    return (
      <Shop fixedCategory="beauty-care" fixedSubcategory="medical-supplies" />
    );
  }
  return <Shop fixedCategory={slug === "daily-care" ? "beauty-care" : slug} />;
}

function NotFoundPage() {
  return (
    <main>
      <PageMeta
        title="Page not found"
        description="The requested page was not found."
      />
      <Empty
        icon={<Search />}
        title="404"
        text="Page not found"
        action="/"
        actionText="Home"
      />
    </main>
  );
}

function App() {
  return (
    <div className="app">
      <Header />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/categories" element={<CategoriesPage />} />
        <Route path="/category/:slug" element={<ShopCategoryRoute />} />
        <Route path="/product/:slug" element={<ProductPage />} />
        <Route
          path="/supplements"
          element={<Shop fixedCategory="supplements" />}
        />
        <Route
          path="/beauty-care"
          element={<Shop fixedCategory="beauty-care" />}
        />
        <Route
          path="/mother-baby"
          element={<Shop fixedCategory="mother-baby" />}
        />
        <Route
          path="/medical-health"
          element={
            <Shop
              fixedCategory="beauty-care"
              fixedSubcategory="medical-supplies"
            />
          }
        />
        <Route
          path="/daily-care"
          element={<Shop fixedCategory="beauty-care" />}
        />
        <Route path="/offers" element={<OffersPage />} />
        <Route path="/wishlist" element={<WishlistPage />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/inquiry" element={<InquiryPage />} />
        <Route path="/checkout" element={<InquiryPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/prescription" element={<PrescriptionPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/health-guide" element={<HealthGuidePage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <Footer />
      <WhatsAppButton />
      <MobileNav />
    </div>
  );
}

export default App;
